import auth0 from 'auth0-js';
import { AUTH_CONFIG } from './../Auth0Config';
import { history } from '../helpers';
import { datadogRum } from '@datadog/browser-rum';

Storage.prototype.setObj = function (key, obj) {
    return this.setItem(key, JSON.stringify(obj))
}
Storage.prototype.getObj = function (key) {
    let item = this.getItem(key);
    if (item) return JSON.parse(item)
    else return null;
}

export default class AuthService {

    MILLISECONDS_TO_NEW_FREE_REVIEW = 90 * 24 * 60 * 60 * 1000;
    MAX_FREE_REVIEWS = 2;

    auth0 = new auth0.WebAuth({
        domain: AUTH_CONFIG.domain,
        clientID: AUTH_CONFIG.clientID,
        redirectUri: AUTH_CONFIG.redirectUri,
        audience: AUTH_CONFIG.audience,
        responseType: 'token id_token',
        scope: 'openid profile user_metadata email'
    });

    login(connection, extraParams = {}) {
        this.auth0.authorize({
            ...(connection ? { connection: connection } : {}),
            nonce: "slidfjoi",

            scope: "openid " +
                " profile" +
                " email" +
                " user_metadata" +
                " create:current_user_metadata" +
                " update:current_user_metadata" +
                " create:current_user_device_credentials",
            ...extraParams
        });
    }

    reauthenticateAfterAccountLink() {
        localStorage.removeItem('access_token');
        localStorage.removeItem('id_token');
        localStorage.removeItem('expires_at');
        localStorage.removeItem('permissions');
        localStorage.removeItem('organization');
        localStorage.removeItem('email');
        localStorage.removeItem('user_id');
        this.login(undefined, { prompt: 'login' });
    }

    continueAsGuest() {
        this.logout();
        this.getGuestId();
        localStorage.setItem('permissions', ['guest']);
        if (!localStorage.getObj('free_reviews')) localStorage.setObj('free_reviews', []);

        this.setUserContextForLogging();
    }

    handleAuthentication(history) {
        this.auth0.parseHash((err, authResult) => {
            if (authResult && authResult.accessToken && authResult.idToken) {
                this.setSession(authResult);
                history.push("/");
            } else if (err) {
                console.log(err);
            }
        });
    }

    setSession(authResult) {
        let expiresAt = JSON.stringify((authResult.expiresIn * 1000) + new Date().getTime());
        localStorage.setItem('access_token', authResult.accessToken);
        localStorage.setItem('id_token', authResult.idToken);
        localStorage.setItem('expires_at', expiresAt);
        localStorage.removeItem('session_mode');
        localStorage.setItem('user_id', authResult.idTokenPayload.sub);
        localStorage.setItem('email', authResult.idTokenPayload.email);
        localStorage.setItem('organization', authResult.idTokenPayload['https://legly.io/organization']);

        let user_authorization = authResult.idTokenPayload["https://legly.io/user_authorization"];
        if (user_authorization) localStorage.setItem('permissions', user_authorization.permissions);
        else localStorage.setItem('permissions', []);

        this.setUserContextForLogging();
    }

    checkSession() {
        var self = this;
        this.auth0.checkSession({}, function (err, authResult) {
            if (!err && authResult) self.setSession(authResult);
            history.push("/");
        });
    }

    setUserContextForLogging() {
        let userId = localStorage.getItem('user_id');
        if (!userId || userId.length <= 6) userId = this.getExistingGuestId() || 'anonymous';
        let permission = "";
        if (this.isCustomer()) permission = 'customer';
        if (this.isGuest()) permission = 'guest';
        if (this.isAdmin()) permission = 'admin';

        datadogRum.removeUser();
        datadogRum.setUser({
            id: userId,
            email: this.getEmail(),
            organization: this.getOrganization(),
            permission
        });
    }

    updateUserId(userId) {
        localStorage.setItem('user_id', userId);
    }

    isAuthenticated() {
        let expiresAt = JSON.parse(localStorage.getItem('expires_at'));
        return new Date().getTime() < expiresAt;
    }

    isAdmin() {
        var permissions = localStorage.getItem('permissions');
        return permissions && permissions.includes("admin");
    }

    isCustomer() {
        var permissions = localStorage.getItem('permissions');
        return permissions && permissions.includes("customer");
    }

    isGuest() {
        var permissions = localStorage.getItem('permissions');
        return permissions && permissions.includes("guest");
    }

    isFreemium() {
        return localStorage.getItem('session_mode') === 'freemium';
    }

    setFreemiumSessionActive(active) {
        if (active) {
            localStorage.setItem('session_mode', 'freemium');
            localStorage.setItem('permissions', ['guest']);
        } else {
            const wasFreemium = this.isFreemium();
            localStorage.removeItem('session_mode');
            if (wasFreemium) localStorage.removeItem('permissions');
        }
    }

    canEditThemes() {
        var permissions = localStorage.getItem('permissions');
        return permissions && permissions.includes("update:themes");
    }

    getOrganization() {
        let org = localStorage.getItem('organization');
        if (org) org = org.toLowerCase();
        return org;
    }

    hasDeviationAccess() {
        let org = this.getOrganization();
        return org === 'bufab' || org === 'fujifilm';
    }

    hasLinkedEmailAccount() {
        var userId = localStorage.getItem('user_id');
        return userId && userId.length >= 6 && !userId.startsWith("sms|") && userId.startsWith("auth0|");
    }

    isUserFromExternalSSO() {
        const userId = localStorage.getItem('user_id');
        if (!userId || userId.length < 6) return false;

        const prefix = userId.split('|')[0];
        const externalPrefixes = new Set([
            'oidc',
            'saml',
            'samlp',
            'oauth2',
            'adfs',
            'waad',
            'google-oauth2',
            'facebook',
            'github',
            'linkedin'
        ]);

        let isSSO = externalPrefixes.has(prefix);
        console.log(`isUserFromExternalSSO: userId=${userId}, isSSO=${isSSO}`);
        return isSSO;
    }

    getUserId = () => {
        return localStorage.getItem('user_id');
    }

    getEmail() {
        return localStorage.getItem('email');
    }

    getEmailDomain = () => {
        let email = this.getEmail();
        if (!email) return "";
        let domain = email.split('@')[1];
        return domain;
    }

    getGuestId() {
        let id = this.getExistingGuestId();
        if (!id) {
            id = 'guest|' + Math.random().toString(36).substr(2, 8) + Math.random().toString(36).substr(2, 8) + Math.random().toString(36).substr(2, 8);
            localStorage.setItem('guest_id', id);
        }
        return id;
    }

    getExistingGuestId() {
        return localStorage.getItem('guest_id');
    }

    getFreeReviewsLeft() {
        if (!this.isGuest()) return 0;
        let freeReviews = localStorage.getObj('free_reviews');
        if (freeReviews) {
            freeReviews = freeReviews.filter(x => x + this.MILLISECONDS_TO_NEW_FREE_REVIEW - Date.now() > 0);
            return Math.max(this.MAX_FREE_REVIEWS - freeReviews.length, 0);
        }
        else return this.MAX_FREE_REVIEWS;
    }

    timeToNewFreeReview() {
        if (!this.isGuest()) return -1;
        let freeReviews = localStorage.getObj('free_reviews');
        if (!freeReviews) return 0;

        freeReviews.sort();
        let earliest = freeReviews[0];
        return Math.max(earliest + this.MILLISECONDS_TO_NEW_FREE_REVIEW - Date.now(), 0);
    }

    consumeFreeReview() {
        if (!this.isGuest()) return;
        let freeReviews = localStorage.getObj('free_reviews');
        if (!freeReviews) freeReviews = [];
        else {
            freeReviews = freeReviews.filter(x => x + this.MILLISECONDS_TO_NEW_FREE_REVIEW - Date.now() > 0);
        }
        if (freeReviews.length < this.MAX_FREE_REVIEWS)
            freeReviews.push(Date.now());

        localStorage.setObj('free_reviews', freeReviews);
    }

    hasShownSurvey() {
        return localStorage.getItem('has_shown_survey') === 'true';
    }

    setHasShownSurvey() {
        localStorage.setItem('has_shown_survey', 'true');
    }

    userAllowsThirdPartyAI() {
        let value = localStorage.getItem('user_allows_third_party');
        return value ? value == 'true' : this.isAdmin() || false && this.isGuest() && !this.isCustomer();
    }

    setUserAllowsThirdPartyAI(value) {
        localStorage.setItem('user_allows_third_party', value == true);
    }

    getAccessToken() {
        const accessToken = localStorage.getObj('access_token');
        if (!accessToken) {
            throw new Error('No access token found');
        }
        return accessToken;
    }

    logout() {
        let isAuthenticated = this.isAuthenticated();
        let isFreemium = this.isFreemium();

        const clearLocalSession = () => {
            localStorage.removeItem('access_token');
            localStorage.removeItem('id_token');
            localStorage.removeItem('expires_at');
            localStorage.removeItem('permissions');
            localStorage.removeItem('organization');
            localStorage.removeItem('email');
            localStorage.removeItem('session_mode');
        };

        if (isFreemium) {
            clearLocalSession();
            fetch('/api/freemium/logout', {
                method: 'POST',
                credentials: 'same-origin'
            }).then(() => {
                window.location.href = '/';
            }, () => {
                window.location.href = '/';
            });
            return;
        }

        clearLocalSession();

        if (isAuthenticated) window.location.href = 'https://legly.eu.auth0.com/v2/logout?returnTo=https://www.legly.io/';
    }
}



// WEBPACK FOOTER //
// ./src/services/auth.service.js